import React from 'react';

/**
 * KD-3581 §9 — a name plus the icon that opens its factsheet in a new tab.
 *
 * The name itself is deliberately NOT clickable: only the icon is. The icon is
 * bound to the last word of the name inside a `whitespace-nowrap` span, so a
 * name that wraps takes the icon with it — an icon alone on the last line reads
 * as an unrelated control.
 */
type ExternalFactsheetLinkProps = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'className' | 'title'
> & {
  /** The name to display. Rendered as plain text, never as a link. */
  name: string;
  /** Factsheet URL. Without one, only the name is rendered. */
  href?: string;
  /** Accessible name of the icon — the apps own the wording. */
  label?: string;
};

const ExternalFactsheetLink = React.forwardRef<
  HTMLSpanElement,
  ExternalFactsheetLinkProps
>(({ name, href, label, ...props }, ref) => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const lastWord = words.length > 0 ? words[words.length - 1] : '';
  const leadingWords = words.slice(0, -1).join(' ');

  if (!href) {
    return (
      <span ref={ref} {...props}>
        {name}
      </span>
    );
  }

  return (
    <span ref={ref} {...props}>
      {leadingWords ? `${leadingWords} ` : null}
      <span className="whitespace-nowrap">
        {lastWord}
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
          title={label}
          onClick={(event) => event.stopPropagation()}
          className="ml-[7px] inline-flex h-[22px] w-[22px] items-center justify-center rounded-md bg-[#F1F5FD] align-middle text-[11px] text-blue-600 no-underline transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <i className="fa-light fa-arrow-up-right-from-square" />
        </a>
      </span>
    </span>
  );
});

ExternalFactsheetLink.displayName = 'ExternalFactsheetLink';

export default ExternalFactsheetLink;
